import { useMemo } from "react";
import {
  RecordContextProvider,
  useGetList,
  useGetMany,
  useListContext,
  useTranslate,
  type Identifier,
} from "ra-core";
import { Link as RouterLink, useLocation } from "react-router";
import { Mail, Phone } from "lucide-react";
import { Badge } from "@/components/ui/badge";

import { Avatar } from "../contacts/Avatar";
import { ageFromDateOfBirth } from "../contacts/playerAge";
import type { ChampClass, Contact, Deal } from "../types";
import { isActiveEnrollment } from "./champFamilyUtils";

/**
 * A family's people, split into guardians (who's the mother, the father) and
 * players (the children, with their age and current classes).
 * Reads the contacts from the surrounding ReferenceManyField.
 */
export const ChampFamilyPeople = ({ familyId }: { familyId: Identifier }) => {
  const translate = useTranslate();
  const { data: people, error, isPending } = useListContext<Contact>();
  const classesByPlayer = useCurrentClassesByPlayer(familyId);

  if (isPending || error) return null;

  const guardians = people.filter((person) => person.role !== "player");
  const players = people.filter((person) => person.role === "player");

  return (
    <div className="flex flex-col gap-6 pt-2">
      <section>
        <h6 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-1">
          {translate("resources.companies.champ.guardians", {
            smart_count: guardians.length,
          })}
        </h6>
        {guardians.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">
            {translate("resources.companies.champ.no_guardians")}
          </p>
        ) : (
          guardians.map((guardian) => (
            <PersonRow key={guardian.id} person={guardian}>
              <GuardianDetails guardian={guardian} />
            </PersonRow>
          ))
        )}
      </section>

      <section>
        <h6 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-1">
          {translate("resources.companies.champ.players", {
            smart_count: players.length,
          })}
        </h6>
        {players.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">
            {translate("resources.companies.champ.no_players")}
          </p>
        ) : (
          players.map((player) => (
            <PersonRow key={player.id} person={player}>
              <PlayerDetails
                player={player}
                classes={classesByPlayer.get(player.id) ?? []}
              />
            </PersonRow>
          ))
        )}
      </section>
    </div>
  );
};

const PersonRow = ({
  person,
  children,
}: {
  person: Contact;
  children: React.ReactNode;
}) => {
  const location = useLocation();
  return (
    <RecordContextProvider value={person}>
      <div className="flex items-start gap-3 py-2 text-sm hover:bg-muted transition-colors">
        <RouterLink
          to={`/contacts/${person.id}/show`}
          state={{ from: location.pathname }}
          className="flex items-start gap-3 flex-1 min-w-0 no-underline"
        >
          <Avatar />
          <div className="flex-1 min-w-0">
            <div className="font-medium">
              {`${person.first_name ?? ""} ${person.last_name ?? ""}`.trim()}
            </div>
            {children}
          </div>
        </RouterLink>
      </div>
    </RecordContextProvider>
  );
};

const GuardianDetails = ({ guardian }: { guardian: Contact }) => {
  const translate = useTranslate();
  const phone = guardian.phone_jsonb?.[0]?.number;
  const email = guardian.email_jsonb?.[0]?.email;
  return (
    <div className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
      <span>
        {guardian.title ||
          translate("resources.companies.champ.relationship_unknown")}
      </span>
      {phone && (
        <span className="inline-flex items-center gap-1">
          <Phone className="w-3 h-3" />
          {phone}
        </span>
      )}
      {email && (
        <span className="inline-flex items-center gap-1">
          <Mail className="w-3 h-3" />
          {email}
        </span>
      )}
    </div>
  );
};

const PlayerDetails = ({
  player,
  classes,
}: {
  player: Contact;
  classes: ChampClass[];
}) => {
  const translate = useTranslate();
  const age = ageFromDateOfBirth(player.date_of_birth);
  return (
    <div className="flex flex-col gap-1">
      <span className="text-muted-foreground">
        {age === null
          ? translate("resources.companies.champ.age_unknown")
          : translate("resources.companies.champ.age", { smart_count: age })}
      </span>
      <div className="flex flex-wrap gap-1">
        {classes.length === 0 ? (
          <span className="text-muted-foreground">
            {translate("resources.companies.champ.no_current_classes")}
          </span>
        ) : (
          classes.map((champClass) => (
            <Badge key={champClass.id} variant="secondary" asChild>
              <RouterLink
                to={`/classes/${champClass.id}/show`}
                onClick={(event) => event.stopPropagation()}
              >
                {champClass.name}
              </RouterLink>
            </Badge>
          ))
        )}
      </div>
    </div>
  );
};

/** Player id → the classes they're actively enrolled in. */
const useCurrentClassesByPlayer = (familyId: Identifier) => {
  const { data: deals = [] } = useGetList<Deal>("deals", {
    filter: { company_id: familyId },
    pagination: { page: 1, perPage: 200 },
    sort: { field: "id", order: "ASC" },
  });
  const activeDeals = deals.filter(
    (deal) => isActiveEnrollment(deal) && deal.player_id && deal.class_id,
  );
  const classIds = [
    ...new Set(activeDeals.map((deal) => deal.class_id as Identifier)),
  ];
  const { data: classes = [] } = useGetMany<ChampClass>(
    "classes",
    { ids: classIds },
    { enabled: classIds.length > 0 },
  );

  return useMemo(() => {
    const classById = new Map(classes.map((c) => [c.id, c]));
    const byPlayer = new Map<Identifier, ChampClass[]>();
    for (const deal of activeDeals) {
      const champClass = classById.get(deal.class_id as Identifier);
      if (!champClass) continue;
      const list = byPlayer.get(deal.player_id as Identifier) ?? [];
      if (!list.some((c) => c.id === champClass.id)) list.push(champClass);
      byPlayer.set(deal.player_id as Identifier, list);
    }
    return byPlayer;
    // activeDeals is derived from deals; deals + classes are the real inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deals, classes]);
};
